import cftLogo from '../assets/images/cftLogo.svg';

/**
 * The CFT mark as a PNG data URL.
 *
 * ⚠️ RASTERISED, NOT EMBEDDED AS SVG. The brand asset is an SVG and neither
 * consumer can take one: jsPDF has no SVG path, and the SpreadsheetML drawing
 * format only accepts raster images that Excel understands. Both get a PNG
 * drawn through a canvas at 4x so it stays crisp when printed or zoomed.
 *
 * ⚠️ RESOLVES TO NULL ON ANY FAILURE, and every caller must treat that as
 * normal. A logo that cannot be loaded must degrade to a typographic lockup —
 * throwing away somebody's invoice because an image did not decode is the worse
 * outcome by a wide margin.
 *
 * Shared by the PDF invoice and the Excel exports so the two documents carry
 * the same mark at the same fidelity; they had separate copies of this until
 * the spreadsheets needed one too.
 */
export const loadLogoPngDataUrl = (scale = 4) =>
    new Promise((resolve) => {
        try {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                try {
                    const canvas = document.createElement('canvas');
                    // The fallbacks matter: an SVG without intrinsic dimensions
                    // reports width 0, which would produce a zero-size canvas and
                    // a blank mark rather than a missing one.
                    canvas.width = (img.width || 93) * scale;
                    canvas.height = (img.height || 100) * scale;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                    resolve({
                        dataUrl: canvas.toDataURL('image/png'),
                        width: img.width || 93,
                        height: img.height || 100,
                    });
                } catch {
                    resolve(null);
                }
            };
            img.onerror = () => resolve(null);
            img.src = cftLogo;
        } catch {
            resolve(null);
        }
    });

/** The raw bytes of that PNG, for callers that write a file rather than draw. */
export const loadLogoPngBytes = async (scale = 4) => {
    const logo = await loadLogoPngDataUrl(scale);
    if (!logo || !logo.dataUrl) return null;
    try {
        const base64 = logo.dataUrl.split(',')[1];
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
        return { bytes, width: logo.width, height: logo.height };
    } catch {
        return null;
    }
};

export default loadLogoPngDataUrl;
